import { useCallback, useEffect, useRef, useState } from "react";
import { usePersistedFilter } from "~/shared/hooks/usePersistedFilter";
import { useMsal } from "@azure/msal-react";
import { useServerSearch } from "~/shared/hooks/useServerSearch";
import type { CallAudit } from "~/features/call-audit/types";
import type { CallAuditPagination } from "~/features/call-audit/types";
import { fetchCallAudits } from "~/features/call-audit/api";
import { ApiError } from "~/shared/utils/api-error";
import { configureMsalInstance } from "~/services/api-client";
import { useTokenErrorHandler } from "~/shared/hooks/useTokenErrorHandler";
import { useLocationCode } from "~/shared/contexts/LocationCodeContext";
import type { Toast } from "~/features/orders/types";
import { CallAuditTable } from "~/features/call-audit/components/CallAuditTable";
import { Toast as ToastComponent } from "~/shared/components/Toast";
import { CenteredLoading } from "~/shared/components/CenteredLoading";
import { ErrorState } from "~/shared/components/ErrorState";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 200;

export default function CallAuditPage() {
  const { instance: msalInstance } = useMsal();
  const { selectedLocationCode } = useLocationCode();

  // State
  const [calls, setCalls] = useState<CallAudit[]>([]);
  const allCitiesSeenRef = useRef<Set<string>>(new Set());
  const [allCities, setAllCities] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState<CallAuditPagination>({
    total: 0,
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Column filters — persisted to localStorage across refreshes
  const [cityFilter, setCityFilter, cityFilterRef] = usePersistedFilter('ca:city');
  const [outcomeFilter, setOutcomeFilter] = usePersistedFilter('ca:outcome');
  const [whiteGloveFilter, setWhiteGloveFilter] = usePersistedFilter('ca:whiteGlove');
  const selectedLocationCodeRef = useRef(selectedLocationCode);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const isInitialMount = useRef(true);

  const { handleTokenError, resetTokenErrorRetry } = useTokenErrorHandler(msalInstance);

  function buildFilters() {
    return {
      cities: cityFilterRef.current.length > 0 ? cityFilterRef.current : undefined,
      locationCodes: selectedLocationCodeRef.current ? [selectedLocationCodeRef.current] : undefined,
    };
  }

  // Data loading
  const load = useCallback(async (reqPage?: number, reqLimit?: number) => {
    if (isLoadingRef.current) return;

    const p = reqPage ?? page;
    const lim = reqLimit ?? pageSize;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const { calls: apiCalls, pagination: pag, contextualCities } = await fetchCallAudits(
        p,
        lim,
        searchQueryRef.current ?? undefined,
        buildFilters()
      );
      setCalls(apiCalls);
      setPagination(pag);
      contextualCities.forEach(c => allCitiesSeenRef.current.add(c));
      setAllCities(Array.from(allCitiesSeenRef.current).sort((a, b) => a.localeCompare(b)));
      setPage(pag.page);
      setError(null);
      resetTokenErrorRetry();
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === "AbortError" || err.message.includes("cancel"))) {
        return;
      }
      if (handleTokenError(err, () => load(p, lim), () => { isLoadingRef.current = false; })) return;

      let errorMessage = "Failed to load call audits";
      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error("Failed to fetch call audits:", err);
      setToast({ message: errorMessage, type: "error" });
      setTimeout(() => setToast(null), 5000);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [page, pageSize, handleTokenError, resetTokenErrorRetry]);

  // Initialize on mount
  useEffect(() => {
    configureMsalInstance(msalInstance);
    load(1, DEFAULT_PAGE_SIZE);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page or pageSize changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    load(page, pageSize);
  }, [page, pageSize, load]);

  // Keep location ref in sync
  useEffect(() => { selectedLocationCodeRef.current = selectedLocationCode; }, [selectedLocationCode]);

  // Ref to always hold the latest load for filter/search triggers
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Re-fetch when location changes
  const prevLocationRef = useRef(selectedLocationCode);
  useEffect(() => {
    if (prevLocationRef.current === selectedLocationCode) return;
    prevLocationRef.current = selectedLocationCode;
    loadRef.current(1, pageSize);
  }, [selectedLocationCode, pageSize]);

  // Server-side search (debounced input + immediate Enter)
  const { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } = useServerSearch({
    onSearch: () => loadRef.current(1, pageSize),
  });

  // Handle city filter change — server-side, triggers API reload
  const handleCityFilterChange = useCallback((selected: string[]) => {
    setCityFilter(selected);
    loadRef.current(1, pageSize);
  }, [pageSize]);

  // Handle outcome filter change — client-side only, no API reload
  const handleOutcomeFilterChange = useCallback((selected: string[]) => {
    setOutcomeFilter(selected);
  }, []);

  // Handle white glove filter change — client-side only, no API reload
  const handleWhiteGloveFilterChange = useCallback((selected: string[]) => {
    setWhiteGloveFilter(selected);
  }, []);

  // Pagination
  const totalPages = pagination.totalPages;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const displayCount = `${pagination.total} total calls`;

  function getPageNumbers(): Array<number | '…'> {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }

  function navigatePage(newPage: number) {
    if (newPage === page) return;
    setPage(newPage);
  }

  // Scroll the table back to the top whenever data changes
  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [calls]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--app-nav-header-height) + var(--app-nav-height))',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--neutral-gray)',
        padding: '8px 32px 8px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >

      {/* ── Fixed top: title strip ── */}
      <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
        <h1 className="page-title">🧾 Call Audit</h1>
        <p className="page-subtitle">Complete log of automated and manual outreach</p>
      </div>

      {/* ── Loading state (initial only) ── */}
      {loading && calls.length === 0 && !error && (
        <CenteredLoading />
      )}

      {/* ── Error state ── */}
      {error && !loading && (
        <ErrorState message={error} onRetry={() => load(page, pageSize)} />
      )}

      {/* ── Main content: scrollable table + pinned pagination ── */}
      {!(loading && calls.length === 0) && !error && (
        <>
          {/* Scrollable table — takes all remaining vertical space */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <CallAuditTable
              calls={calls}
              loading={loading}
              emptyMessage={calls.length === 0 ? "No calls yet" : "No calls found"}
              scrollRef={tableScrollRef}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearchSubmit}
              cityFilter={cityFilter}
              onCityFilterChange={handleCityFilterChange}
              cityOptions={allCities}
              outcomeFilter={outcomeFilter}
              onOutcomeFilterChange={handleOutcomeFilterChange}
              whiteGloveFilter={whiteGloveFilter}
              onWhiteGloveFilterChange={handleWhiteGloveFilterChange}
            />
          </div>

          {/* ── Pinned pagination bar ── */}
          <div
            className="autocall-pagination"
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                padding: '10px 0',
                borderTop: '1px solid var(--border)',
                background: 'var(--neutral-gray)',
              }}
            >
              {/* Page size selector */}
              <span style={{ fontWeight: 500 }}>Orders per page:</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={pageSize === size ? 'primary' : 'secondary'}
                    onClick={() => { setPageSize(size); navigatePage(1); }}
                    style={{ minWidth: 40 }}
                  >
                    {size}
                  </button>
                ))}
              </div>

              {/* Page navigation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => navigatePage(Math.max(1, page - 1))}
                  disabled={!canPrev}
                  style={{ minWidth: 36 }}
                >
                  ‹
                </button>
                {getPageNumbers().map((pg, i, arr) =>
                  pg === '…' ? (
                    <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
                  ) : (
                    <button
                      key={pg}
                      type="button"
                      className={page === pg ? 'primary' : 'secondary'}
                      onClick={() => navigatePage(pg)}
                      style={{ minWidth: 36 }}
                    >
                      {pg}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="secondary"
                  onClick={() => navigatePage(Math.min(totalPages, page + 1))}
                  disabled={!canNext}
                  style={{ minWidth: 36 }}
                >
                  ›
                </button>
              </div>

              {/* Total count */}
              <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {displayCount}
              </span>

              {/* Refresh */}
              <button
                type="button"
                className="secondary"
                onClick={() => load(page, pageSize)}
                aria-label="Refresh"
              >
                Refresh
              </button>
          </div>
        </>
      )}

      {toast && <ToastComponent toast={toast} />}
    </div>
  );
}
